import { useDispatch } from "react-redux";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { actionAuthSignOutUser } from "../Auth";
import { clearCache, MGIS_DOC_LINK } from "../Shared/util";
import { useRef, useState } from "react";
import Typography from "@mui/material/Typography";

const root_ = (theme) => ({
  width: 200,
  padding: "10px",
  border: "1px rgba(0, 0, 0, 0.2) solid",
});

const btn_ = (theme) => ({
  marginLeft: "auto",
});

const menuItem_ = (theme) => ({
  minHeight: "44px !important",
});

// Make this look like a link.
// If we use an <a> tag, the clickable region is too
// small and Siteimprove complains. Reworked these
// so the clickable region is the whole rectangle
// but it still looks/acts like a link.
const linkItem_ = (theme) => ({
  color: "primary.main",
  textDecoration: "underline",
  whiteSpace: "nowrap",
  "&:hover": {
    color: "primary.dark",
  },
});

export default function UserMenu() {
  const dispatch = useDispatch();
  const anchorRef = useRef(null);
  const [expandUser, setExpandUser] = useState(false);

  const handleExpandUserClick = () => {
    setExpandUser(!expandUser);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setExpandUser(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setExpandUser(false);
    }
  }

  const signoutHandler = (event) => {
    dispatch(actionAuthSignOutUser());
    handleClose(event);
  };

  const clearCacheHandler = () => {
    if (clearCache()) {
      dispatch(actionAuthSignOutUser());
    }
  };

  const openLink = (url) => (event) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Tooltip title="More Options">
        <IconButton
          ref={anchorRef}
          onClick={handleExpandUserClick}
          sx={btn_}
          size="large"
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Popper
        open={expandUser}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={expandUser ? { zIndex: 1 } : {}}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper sx={root_}>
              <ClickAwayListener onClickAway={handleClose}>
                {/* tabindex="" avoids a spurious complaint from Siteimprove
                    and does not seem to cause any navigation issues.
                    It overrides a tabindex="-1" which MenuList would
                    have put there otherwise. */}
                <MenuList
                  tabindex=""
                  autoFocus={true}
                  autoFocusItem={expandUser}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  <MenuItem
                    sx={menuItem_}
                    onClick={openLink("/home/index.html#about-mgis")}
                  >
                    <Tooltip title="About MGIS">
                      <Typography
                        // Causes <h2> rather than <p>.
                        // Since Typography adda an aria-label onto this,
                        // Siteimprove complaines if this is a <p> since
                        // arial-label is not defined for <p>. It is
                        // defined for <h2> (and the h2 seems to not mess
                        // up the styling).
                        variant="subtitle1"
                        sx={linkItem_}
                      >
                        About
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem sx={menuItem_} onClick={openLink(MGIS_DOC_LINK)}>
                    <Tooltip title="MGIS Documentation">
                      <Typography variant="subtitle1" sx={linkItem_}>
                        Help
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem
                    sx={menuItem_}
                    onClick={openLink("mailto:mgis-help@umich.edu")}
                  >
                    <Tooltip title="Email MGIS Help">
                      <Typography variant="subtitle1" sx={linkItem_}>
                        Email MGIS
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    sx={menuItem_}
                    onClick={openLink("https://www.umms.med.umich.edu/mspace/")}
                  >
                    <Tooltip title="Space Attribute Reporting Tool">
                      <Typography variant="subtitle1" sx={linkItem_}>
                        M-Space
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem
                    sx={menuItem_}
                    onClick={openLink("https://wolverineaccess.umich.edu")}
                  >
                    <Tooltip title="U-M Tools and Resources">
                      <Typography variant="subtitle1" sx={linkItem_}>
                        Wolverine Access
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                  <Divider />
                  <MenuItem sx={menuItem_} onClick={clearCacheHandler}>
                    Clear Cache
                  </MenuItem>
                  <Divider />
                  <MenuItem sx={menuItem_} onClick={signoutHandler}>
                    Logout
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
